import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import styled, { css } from "styled-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Switch,
  Input,
  Button,
  message,
  Card,
  Typography,
  Spin,
  Modal,
  Tag,
  Tooltip,
} from "antd";
import {
  CopyOutlined,
  PlusOutlined,
  DeleteOutlined,
  LinkOutlined,
  HomeOutlined,
  StarOutlined,
  DownOutlined,
  RightOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { patchUnit, extractLeaseRules } from "../../lib/api/unitsApi";
import { discordTrack } from "../../lib/analytics/discordTrack";
import { AuthContext } from "../../context/AuthProvider";
import unitModel from "../../lib/firebase/unitModel";
import { getUnitDisplayAddress } from "../../lib/unitAddressUtils";
import { getEffectiveUnitCap } from "../../lib/pricing";
import {
  hasFullOperatingSystemAccess,
  hasSuiteScaleOperatingSystemPlan,
} from "../../helpers/accountProduct";
import { OPS_CHAT_MAX_CONCURRENT_TENANT_CHATS } from "../../helpers/constants";
import { propertyKeys, userKeys } from "../../lib/queryKeys";
import userModel from "../../lib/firebase/userModel";
import { device } from "../../lib/breakpoints";

/** Magic link host matches the app origin (localhost, sandbox, prod) with env/SSR fallbacks. */
function getChatAppBaseUrl() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return process.env.REACT_APP_FRONTEND_URL || "https://landlordops.com";
}

const { TextArea } = Input;
const { Text } = Typography;

/** URL-safe slug: address/name + id suffix to reduce collisions across doors. */
function generateLeoChatSlugFromUnit(unit) {
  const raw =
    getUnitDisplayAddress(unit) || (unit && unit.name) || "door";
  let base = String(raw)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  if (!base) base = "unit";
  const idPart = String((unit && unit.id) || "")
    .replace(/[^a-f0-9]/gi, "")
    .slice(-6);
  const tail = idPart || String(Date.now()).slice(-6);
  return `${base}-${tail}`.replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 64);
}

const MIN_CHAT_SLUG_LENGTH = 5;
const FOREST_GREEN = "#2C5F2D";
/** Card outer radius; door header uses inner radius so green meets the 2px card border */
const DOOR_CARD_RADIUS = "12px";
const DOOR_CARD_HEADER_RADIUS_INNER = "10px";

/** Strips client-only fields before Firestore / PATCH. */
function stripKbForApi(kb) {
  if (!Array.isArray(kb)) return [];
  return kb.map((e) => {
    const title = (e.title || "").trim() || "Untitled entry";
    const content = (e.content || "").trim();
    return {
      id: e.id,
      type: ["faq", "rule", "document"].includes(e.type) ? e.type : "faq",
      title: title.slice(0, 200),
      content: content.slice(0, 5000),
      createdAt: e.createdAt,
      updatedAt: e.updatedAt,
      ...(e.source === "lease_extract" ? { source: "lease_extract" } : {}),
    };
  });
}

const VISITOR_LABEL_DEBOUNCE_MS = 1500;

/** Plain Input (no prefix/suffix) so we avoid ant-input-affix-wrapper double-border / layout shift. */
const VisitorLabelTextInput = styled(Input)`
  flex: 1;
  min-width: 0;
  border-radius: 8px !important;

  /* Ant Design 5: real input is nested; center value + placeholder */
  && .ant-input,
  && input.ant-input,
  && input {
    text-align: center;
  }
`;

const VisitorLabelInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
`;

/** Fixed slot so the spinner does not narrow the field or move text when it appears. */
const VisitorLabelSaveStatus = styled.div`
  width: 1.375rem;
  height: 1.375rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${FOREST_GREEN};
`;

/**
 * Optional tenant name / household label for chat: debounced save after typing stops, and on blur.
 */
function VisitorLabelAutoField({ unit, patchUnitsList, onSave, saving }) {
  const timerRef = useRef(null);
  const latestValueRef = useRef(String(unit.leoChatVisitorLabel ?? ""));
  const lastSavedRef = useRef(
    String(unit.leoChatVisitorLabel ?? "").trim()
  );

  // Baseline when switching units only; do not re-sync on every `leoChatVisitorLabel` prop change
  // (avoids clobbering debounce / in-progress edits when the list re-renders).
  useEffect(() => {
    const s = String(unit.leoChatVisitorLabel ?? "");
    lastSavedRef.current = s.trim();
    latestValueRef.current = s;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally unit.id only
  }, [unit.id]);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    []
  );

  const flushSave = useCallback(
    async (raw) => {
      const trimmed = String(raw ?? "").trim();
      if (trimmed === lastSavedRef.current) return;
      try {
        await onSave(unit.id, raw);
        lastSavedRef.current = trimmed;
      } catch {
        // Error toast from onSave; do not update lastSaved
      }
    },
    [onSave, unit.id]
  );

  const scheduleSave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void flushSave(latestValueRef.current);
    }, VISITOR_LABEL_DEBOUNCE_MS);
  }, [flushSave]);

  const onChange = (e) => {
    const v = e.target.value;
    latestValueRef.current = v;
    patchUnitsList((prev) =>
      prev.map((u) => (u.id === unit.id ? { ...u, leoChatVisitorLabel: v } : u))
    );
    scheduleSave();
  };

  const onBlur = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    void flushSave(latestValueRef.current);
  };

  return (
    <VisitorLabelInputRow>
      <VisitorLabelTextInput
        value={unit.leoChatVisitorLabel ?? ""}
        onChange={onChange}
        onBlur={onBlur}
        placeholder="e.g. Jamie, or 2A household"
        maxLength={200}
        aria-busy={saving || undefined}
      />
      <VisitorLabelSaveStatus aria-hidden={!saving}>
        {saving ? <Spin size="small" /> : null}
      </VisitorLabelSaveStatus>
    </VisitorLabelInputRow>
  );
}

export default function TenantChatSettings() {
  const { user, userInfo, isOpsChatOnlyUser } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [saving, setSaving] = useState({});
  const [emailCcSaving, setEmailCcSaving] = useState(false);
  const [leaseReading, setLeaseReading] = useState({});
  /** If a unit id is in this set, its detail panel is expanded (default: all collapsed). */
  const [expandedPropertyIds, setExpandedPropertyIds] = useState(
    () => new Set()
  );
  const leaseFileInputRef = useRef(null);
  const pendingLeaseUnitIdRef = useRef(null);

  const togglePropertyPanel = useCallback((unitId) => {
    setExpandedPropertyIds((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  }, []);

  const {
    data: unitsRaw,
    isLoading: loading,
    isError,
  } = useQuery({
    queryKey: propertyKeys.units(user?.uid),
    queryFn: () => unitModel.getByOwner(user.uid),
    enabled: !!user?.uid,
    refetchOnWindowFocus: true,
  });

  const units = useMemo(() => {
    const list = [...(unitsRaw ?? [])];
    list.sort((a, b) => {
      const addrA = getUnitDisplayAddress(a) || a.name || a.id || "";
      const addrB = getUnitDisplayAddress(b) || b.name || b.id || "";
      return addrA.localeCompare(addrB);
    });
    return list;
  }, [unitsRaw]);

  const enabledTenantChatCount = useMemo(
    () => (units || []).filter((u) => u.leoChatEnabled).length,
    [units]
  );
  const opsChatAtConcurrentChatLimit =
    isOpsChatOnlyUser &&
    enabledTenantChatCount >= OPS_CHAT_MAX_CONCURRENT_TENANT_CHATS;

  const planDoorCap = useMemo(() => {
    if (!userInfo) return null;
    return getEffectiveUnitCap(
      userInfo,
      userInfo.pricingTierGroup || "market_2026"
    );
  }, [userInfo]);

  /** Suite Scale / Enterprise (full OS): tenant chat is always on; per-door toggles are hidden. */
  const isFullSuiteOsUser = hasFullOperatingSystemAccess(userInfo);

  const emailCcOnTenantMessages = userInfo?.emailCcOnTenantMessages === true;

  const handleEmailCcToggle = useCallback(
    async (checked) => {
      if (!user?.uid) return;
      setEmailCcSaving(true);
      try {
        await userModel.update({
          id: user.uid,
          data: { emailCcOnTenantMessages: checked },
        });
        await queryClient.invalidateQueries({
          queryKey: userKeys.user(user.uid),
        });
        message.success(
          checked
            ? "You will be copied on tenant emails"
            : "Email copy turned off"
        );
      } catch (err) {
        console.error("[TenantChatSettings] email CC toggle failed:", err);
        message.error("Could not update email notification setting");
      } finally {
        setEmailCcSaving(false);
      }
    },
    [queryClient, user?.uid]
  );

  const loadErrorShownRef = useRef(false);
  useEffect(() => {
    if (!isError || loadErrorShownRef.current) return;
    loadErrorShownRef.current = true;
    message.error("Failed to load doors");
  }, [isError]);

  const patchUnitsList = useCallback(
    (updater) => {
      if (!user?.uid) return;
      queryClient.setQueryData(propertyKeys.units(user.uid), (old) =>
        updater(Array.isArray(old) ? old : [])
      );
    },
    [queryClient, user?.uid]
  );

  const unitCardTitle = useCallback((unit) => {
    const addr = getUnitDisplayAddress(unit);
    if (addr && unit.name) return `${addr} (${unit.name})`;
    return addr || unit.name || "Rental door";
  }, []);

  const handleToggle = useCallback(
    async (unitId, enabled, options = {}) => {
      const { quiet } = options;
      setSaving((s) => ({ ...s, [unitId]: true }));
      try {
        const row = (units || []).find((u) => u.id === unitId);
        const payload = { leoChatEnabled: enabled };
        const hadNoSlug = enabled && row && !String(row.leoChatSlug || "").trim();
        if (hadNoSlug) {
          const gen = generateLeoChatSlugFromUnit(row);
          if (String(gen).length < MIN_CHAT_SLUG_LENGTH) {
            payload.leoChatSlug = `${gen}-link`.slice(0, 64);
          } else {
            payload.leoChatSlug = gen;
          }
        }
        await patchUnit(unitId, payload);
        if (enabled && hadNoSlug && payload.leoChatSlug) {
          patchUnitsList((prev) =>
            prev.map((u) => (u.id === unitId ? { ...u, ...payload } : u))
          );
        }
        await queryClient.invalidateQueries({
          queryKey: propertyKeys.units(user?.uid),
        });
        if (!quiet) {
          message.success(
            enabled
              ? hadNoSlug
                ? "Tenant chat on. We set a link slug you can change anytime."
                : "Tenant chat enabled"
              : "Tenant chat disabled"
          );
        }
      } catch (err) {
        if (!quiet) {
          const text = err?.message || "Failed to update";
          const isLimit =
            err?.status === 403 && err?.code === "UNIT_LIMIT_REACHED";
          message.error(text, isLimit ? 10 : 4);
        } else {
          throw err;
        }
      } finally {
        setSaving((s) => ({ ...s, [unitId]: false }));
      }
    },
    [queryClient, user?.uid, units, patchUnitsList]
  );

  const bulkChatEnableKeyRef = useRef("");
  useEffect(() => {
    if (!hasSuiteScaleOperatingSystemPlan(userInfo) || !user?.uid || loading) {
      return;
    }
    const disabled = (units || []).filter((u) => !u.leoChatEnabled);
    if (!disabled.length) return;

    const chatCap =
      planDoorCap != null && Number.isFinite(planDoorCap)
        ? planDoorCap
        : 150;
    const slotsLeft = Math.max(0, chatCap - enabledTenantChatCount);
    if (slotsLeft === 0) return;

    const batch = disabled.slice(0, slotsLeft);
    const runKey = `${user.uid}:${batch.map((u) => u.id).join(",")}`;
    if (bulkChatEnableKeyRef.current === runKey) return;
    bulkChatEnableKeyRef.current = runKey;

    let cancelled = false;
    (async () => {
      let succeeded = 0;
      let lastError = null;
      for (const u of batch) {
        if (cancelled) return;
        try {
          await handleToggle(u.id, true, { quiet: true });
          succeeded += 1;
        } catch (err) {
          lastError = err;
        }
      }
      if (cancelled) return;
      if (succeeded > 0) {
        message.success(
          succeeded === 1
            ? "Tenant chat is on for this door (included with your plan)."
            : `Tenant chat is on for ${succeeded} door${succeeded === 1 ? "" : "s"} (included with your plan).`,
          3.5
        );
      } else if (lastError) {
        const text = lastError?.message || "Could not enable tenant chat";
        const isLimit =
          lastError?.status === 403 &&
          lastError?.code === "UNIT_LIMIT_REACHED";
        message.error(text, isLimit ? 8 : 4);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    userInfo,
    user?.uid,
    loading,
    units,
    handleToggle,
    planDoorCap,
    enabledTenantChatCount,
  ]);

  const handleVisitorLabelSave = useCallback(
    async (unitId, raw) => {
      const v = String(raw ?? "").trim();
      setSaving((prev) => ({ ...prev, [`vlabel-${unitId}`]: true }));
      try {
        await patchUnit(unitId, {
          leoChatVisitorLabel: v || null,
        });
        await queryClient.invalidateQueries({
          queryKey: propertyKeys.units(user?.uid),
        });
        const successText = v
          ? "Tenant name saved"
          : "Tenant name cleared";
        message.success(successText, 2.5);
      } catch (err) {
        message.error(err.message || "Failed to save name");
        throw err;
      } finally {
        setSaving((prev) => ({ ...prev, [`vlabel-${unitId}`]: false }));
      }
    },
    [queryClient, user?.uid]
  );

  const handleSlugSave = useCallback(async (unitId, slug) => {
    const s = String(slug ?? "").trim();
    if (!s) {
      message.error("Enter a chat link slug to save (cannot be empty).");
      return;
    }
    if (s.length < MIN_CHAT_SLUG_LENGTH) {
      message.error(
        `The chat link slug must be at least ${MIN_CHAT_SLUG_LENGTH} characters.`
      );
      return;
    }
    setSaving((prev) => ({ ...prev, [`slug-${unitId}`]: true }));
    try {
      await patchUnit(unitId, { leoChatSlug: s });
      await queryClient.invalidateQueries({
        queryKey: propertyKeys.units(user?.uid),
      });
      message.success("Chat link saved");
    } catch (err) {
      message.error(err.message || "Failed to save slug");
    } finally {
      setSaving((prev) => ({ ...prev, [`slug-${unitId}`]: false }));
    }
  }, [queryClient, user?.uid]);

  const handleCopyLink = useCallback((slug) => {
    const base = getChatAppBaseUrl();
    const url = `${base}/chat/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      message.success("Link copied to clipboard");
    });
  }, []);

  const openLeaseFilePicker = useCallback((unitId) => {
    pendingLeaseUnitIdRef.current = unitId;
    leaseFileInputRef.current?.click();
  }, []);

  const handleLeaseFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      const unitId = pendingLeaseUnitIdRef.current;
      e.target.value = "";
      if (!file || !unitId) return;

      setLeaseReading((s) => ({ ...s, [unitId]: true }));
      try {
        const data = await extractLeaseRules(unitId, file);
        const rules = Array.isArray(data.rules) ? data.rules : [];
        if (data.warning) {
          message.info(data.warning, 6);
        }
        if (rules.length === 0) {
          message.info(
            data.message ||
              "No rules were extracted. Try a text-based PDF or add entries manually."
          );
          return;
        }
        const now = new Date().toISOString();
        const newEntries = rules.map((r, i) => ({
          id: `kb-lease-${Date.now()}-${i}-${Math.random()
            .toString(36)
            .slice(2, 9)}`,
          type: "rule",
          title: (r.title || "").trim() || "Rule from lease",
          content: (r.content || "").trim(),
          createdAt: now,
          updatedAt: now,
          source: "lease_extract",
          _draft: true,
        }));
        patchUnitsList((prev) =>
          prev.map((u) =>
            u.id === unitId
              ? {
                  ...u,
                  knowledgeBase: [...(u.knowledgeBase || []), ...newEntries],
                }
              : u
          )
        );
        message.success(
          `Added ${newEntries.length} draft rule(s). Review, edit, then save.`
        );
        discordTrack("lease_rules_extracted", {
          unitId,
          count: newEntries.length,
        });
      } catch (err) {
        message.error(err.message || "Could not read the lease");
      } finally {
        setLeaseReading((s) => {
          const n = { ...s };
          delete n[unitId];
          return n;
        });
        pendingLeaseUnitIdRef.current = null;
      }
    },
    [patchUnitsList]
  );

  const handleKBAdd = useCallback(
    async (unitId, currentKB) => {
      const newEntry = {
        id: `kb-${Date.now()}`,
        type: "faq",
        title: "",
        content: "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const updated = [...(currentKB || []), newEntry];
      patchUnitsList((prev) =>
        prev.map((u) =>
          u.id === unitId ? { ...u, knowledgeBase: updated } : u
        )
      );
    },
    [patchUnitsList]
  );

  const performKBRemove = useCallback(
    async (unitId, entryId, currentKB) => {
      const updated = (currentKB || []).filter((e) => e.id !== entryId);
      setSaving((s) => ({ ...s, [`kb-${unitId}`]: true }));
      try {
        await patchUnit(unitId, { knowledgeBase: stripKbForApi(updated) });
        await queryClient.invalidateQueries({
          queryKey: propertyKeys.units(user?.uid),
        });
        message.success("Entry removed");
      } catch {
        message.error("Failed to remove entry");
      } finally {
        setSaving((s) => ({ ...s, [`kb-${unitId}`]: false }));
      }
    },
    [queryClient, user?.uid]
  );

  const requestKBRemove = useCallback(
    (unitId, entryId, currentKB, entryTitle) => {
      const raw = (entryTitle || "").trim();
      const short =
        raw.length > 80 ? `${raw.slice(0, 80)}...` : raw;
      const label = short || "Untitled entry";
      Modal.confirm({
        title: "Remove this knowledge base entry?",
        icon: null,
        centered: true,
        content: `"${label}" will be removed for this door. You can add a new entry later.`,
        okText: "Remove",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        onOk: () => performKBRemove(unitId, entryId, currentKB),
      });
    },
    [performKBRemove]
  );

  const cloneKBWithFreshIds = (entries) =>
    (Array.isArray(entries) ? entries : []).map((e) => ({
      ...e,
      id: `kb-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));

  const handleApplyKBToAllOtherDoors = useCallback(
    (sourceUnitId, kb) => {
      if (!user?.uid) return;
      if (!Array.isArray(kb) || kb.length === 0) {
        message.info("Add at least one entry to copy to your other doors.");
        return;
      }
      const others = (units || []).filter((u) => u.id !== sourceUnitId);
      if (others.length === 0) {
        message.info(
          "You only have this door. Add more doors on Doors to use bulk copy."
        );
        return;
      }
      Modal.confirm({
        title: "Apply this knowledge base to all other doors?",
        icon: null,
        centered: true,
        content: `We’ll copy these ${kb.length} entr${kb.length === 1 ? "y" : "ies"} to your other ${others.length} door${others.length === 1 ? "" : "s"}. Saved entries on each door are replaced. You can edit per door after.`,
        okText: "Apply to all",
        onOk: async () => {
          const hide = message.loading("Copying to other doors…", 0);
          try {
            for (const u of others) {
              // eslint-disable-next-line no-await-in-loop
              await patchUnit(u.id, {
                knowledgeBase: cloneKBWithFreshIds(stripKbForApi(kb)),
              });
            }
            await queryClient.invalidateQueries({
              queryKey: propertyKeys.units(user.uid),
            });
            message.success("Knowledge base copied to all other doors.");
            discordTrack("knowledge_base_bulk_applied", {
              fromUnit: sourceUnitId,
              toCount: others.length,
              entryCount: kb.length,
            });
          } catch {
            message.error("Some doors could not be updated. Try again.");
          } finally {
            hide();
          }
        },
      });
    },
    [units, user, queryClient]
  );

  const handleKBUpdate = useCallback(
    (unitId, entryId, field, value) => {
      patchUnitsList((prev) =>
        prev.map((u) => {
          if (u.id !== unitId) return u;
          const kb = (u.knowledgeBase || []).map((e) =>
            e.id === entryId ? { ...e, [field]: value } : e
          );
          return { ...u, knowledgeBase: kb };
        })
      );
    },
    [patchUnitsList]
  );

  const handleKBSave = useCallback(async (unitId, kb) => {
    setSaving((s) => ({ ...s, [`kb-${unitId}`]: true }));
    try {
      await patchUnit(unitId, { knowledgeBase: stripKbForApi(kb) });
      await queryClient.invalidateQueries({
        queryKey: propertyKeys.units(user?.uid),
      });
      message.success("Knowledge base saved");
      discordTrack("knowledge_base_updated", {
        unitId,
        entryCount: kb.length,
      });
    } catch {
      message.error("Failed to save knowledge base");
    } finally {
      setSaving((s) => ({ ...s, [`kb-${unitId}`]: false }));
    }
  }, [queryClient, user?.uid]);

  if (loading) {
    return (
      <Center>
        <Spin />
      </Center>
    );
  }

  if (!units.length) {
    return (
      <EmptyState>
        <HomeOutlined style={{ fontSize: "2.5rem", color: "var(--color-primary-darker, #2C5F2D)", opacity: 0.5 }} />
        <EmptyTitle>No rental doors yet</EmptyTitle>
        <EmptyText>
          Add doors on <strong>Doors</strong> (your rent roll). Each door gets its
          own tenant chat row and magic link, even when several doors share the
          same street address.
        </EmptyText>
        <AddDoorsNavButton
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/units")}
        >
          Add doors
        </AddDoorsNavButton>
      </EmptyState>
    );
  }

  return (
    <Wrapper>
      <SectionDescription>
        {isFullSuiteOsUser
          ? "Each door below has its own magic link and Leo chat thread. Copy the link and send it to that household."
          : "Turn Leo on per door, then copy each magic link for that household."}
      </SectionDescription>

      <AutomatedEmailsPanel>
        <EmailCcSettingRow>
          <EmailCcLabelBlock>
            <SettingLabel>Copy me on emails sent to tenants</SettingLabel>
            <EmailCcCaption>
              Rent reminders and welcome emails only. Tenants will see your email
              and can Reply-All.
            </EmailCcCaption>
          </EmailCcLabelBlock>
          <EmailCcSwitchWrap>
            <Switch
              checked={emailCcOnTenantMessages}
              loading={emailCcSaving}
              onChange={handleEmailCcToggle}
              aria-label="Copy me on emails sent to tenants"
            />
          </EmailCcSwitchWrap>
        </EmailCcSettingRow>
      </AutomatedEmailsPanel>

      <DoorsDivider aria-hidden />

      <input
        ref={leaseFileInputRef}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: "none" }}
        onChange={handleLeaseFileChange}
        aria-hidden
      />

      {units.map((unit, index) => {
        const slug = unit.leoChatSlug || "";
        const slugTrim = String(slug).trim();
        const slugMeetsMin = slugTrim.length >= MIN_CHAT_SLUG_LENGTH;
        const kb = unit.knowledgeBase || [];
        const magicLink =
          slugMeetsMin && slugTrim
            ? `${getChatAppBaseUrl()}/chat/${slugTrim}`
            : "";
        const leaseLoading = !!leaseReading[unit.id];
        const propertyListNum = index + 1;
        const addressLine = unitCardTitle(unit);
        const panelExpanded = expandedPropertyIds.has(unit.id);
        const chatOn = !!unit.leoChatEnabled;
        const displayChatOn = isFullSuiteOsUser || chatOn;
        const panelLabelAction = panelExpanded ? "Collapse" : "Expand";

        return (
          <PropertyCard
            key={unit.id}
            size="small"
            $bodyHidden={!panelExpanded}
            title={
              <PropertyCardHead
                type="button"
                onClick={() => togglePropertyPanel(unit.id)}
                aria-expanded={panelExpanded}
                aria-label={`${panelLabelAction} property ${propertyListNum}. ${addressLine}. Tenant chat is ${displayChatOn ? "on" : "off"}.`}
              >
                <PropertyHeadText>
                  <PropertyNum>Door #{propertyListNum}</PropertyNum>
                  <PropertyAddressLine>{addressLine}</PropertyAddressLine>
                </PropertyHeadText>
                <HeaderStatusCluster>
                  <LeoHeaderStatus $on={displayChatOn}>
                    {displayChatOn ? "On" : "Off"}
                  </LeoHeaderStatus>
                  <HeadChevronWrap>
                    {panelExpanded ? <DownOutlined /> : <RightOutlined />}
                  </HeadChevronWrap>
                </HeaderStatusCluster>
              </PropertyCardHead>
            }
          >
            {!isFullSuiteOsUser && (
              <SettingRow>
                <SettingLabel>Enable tenant chat</SettingLabel>
                {opsChatAtConcurrentChatLimit && !unit.leoChatEnabled ? (
                  <Tooltip
                    title={`You can have up to ${OPS_CHAT_MAX_CONCURRENT_TENANT_CHATS} doors with tenant chat on at once. Turn off chat on another door to enable it here.`}
                  >
                    <span>
                      <Switch
                        checked={!!unit.leoChatEnabled}
                        onChange={(checked) => handleToggle(unit.id, checked)}
                        loading={saving[unit.id]}
                        disabled
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Switch
                    checked={!!unit.leoChatEnabled}
                    onChange={(checked) => handleToggle(unit.id, checked)}
                    loading={saving[unit.id]}
                  />
                )}
              </SettingRow>
            )}

            {(isFullSuiteOsUser || unit.leoChatEnabled) && (
              <>
                <ChatConfigPanel>
                  <ChatSlugField>
                    <SettingLabel>Chat slug</SettingLabel>
                    <SlugRow>
                      <SlugInput
                        value={slug}
                        onChange={(e) => {
                          const v = e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9-]/g, "");
                          patchUnitsList((prev) =>
                            prev.map((u) =>
                              u.id === unit.id ? { ...u, leoChatSlug: v } : u
                            )
                          );
                        }}
                        placeholder="my-unit-slug"
                        addonBefore="/chat/"
                        status={
                          slugTrim.length > 0 && !slugMeetsMin
                            ? "error"
                            : undefined
                        }
                      />
                      <SlugSaveBtn
                        type="primary"
                        onClick={() => handleSlugSave(unit.id, slug)}
                        loading={saving[`slug-${unit.id}`]}
                        disabled={!slugMeetsMin}
                      >
                        Save
                      </SlugSaveBtn>
                    </SlugRow>
                    <ChatSlugHelper>
                      <Text
                        type={
                          slugTrim.length > 0 && !slugMeetsMin
                            ? "danger"
                            : "secondary"
                        }
                        style={{
                          fontSize: 12,
                          lineHeight: 1.4,
                          display: "block",
                        }}
                      >
                        At least {MIN_CHAT_SLUG_LENGTH} characters (letters,
                        numbers, or hyphens) to save and show the magic link.
                      </Text>
                    </ChatSlugHelper>
                  </ChatSlugField>

                  {magicLink && (
                    <MagicLinkBlock>
                      <MagicLinkLabel>
                        <LinkOutlined /> Magic link
                      </MagicLinkLabel>
                      <MagicUrlCodeRow>
                        <MagicUrlCodeInput
                          readOnly
                          size="small"
                          value={magicLink}
                          aria-label="Full tenant chat URL to copy"
                          styles={{
                            input: {
                              fontFamily:
                                'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                              fontSize: 12.5,
                              lineHeight: 1.45,
                              color: "#183018",
                              background: "#e4eee4",
                            },
                          }}
                          onFocus={(e) => {
                            const el = e?.target;
                            if (el && typeof el.select === "function")
                              el.select();
                          }}
                          onClick={(e) => {
                            const el = e?.target;
                            if (el && typeof el.select === "function")
                              el.select();
                          }}
                        />
                        <CopyUrlBtn
                          type="primary"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => handleCopyLink(slugTrim)}
                          disabled={!slugMeetsMin}
                        >
                          Copy link
                        </CopyUrlBtn>
                      </MagicUrlCodeRow>
                    </MagicLinkBlock>
                  )}

                  <VisitorLabelBlock $opsChatCenter={isOpsChatOnlyUser}>
                    <VisitorAddressSection>
                      <VisitorAddressLabelGroup $opsChatCenter={isOpsChatOnlyUser}>
                        <SettingLabel>
                          What’s your tenant’s name?
                        </SettingLabel>
                        <Tooltip
                          title={
                            'Optional. Shown in chat (first name, nickname, or a label like "the 2A household"). Auto-saves about 1.5s after you stop typing (or when you leave the field), with a short confirmation. Stored on this unit in LandlordOps.'
                          }
                          placement="topLeft"
                        >
                          <VisitorAddressInfoIcon
                            tabIndex={0}
                            aria-label="More about how Leo uses this"
                          >
                            <InfoCircleOutlined />
                          </VisitorAddressInfoIcon>
                        </Tooltip>
                      </VisitorAddressLabelGroup>
                      <VisitorAddressFieldRow $opsChatCenter={isOpsChatOnlyUser}>
                        <VisitorAddressInputWrap $opsChatCenter={isOpsChatOnlyUser}>
                          <VisitorLabelAutoField
                            unit={unit}
                            patchUnitsList={patchUnitsList}
                            onSave={handleVisitorLabelSave}
                            saving={!!saving[`vlabel-${unit.id}`]}
                          />
                        </VisitorAddressInputWrap>
                      </VisitorAddressFieldRow>
                    </VisitorAddressSection>
                  </VisitorLabelBlock>
                </ChatConfigPanel>

                <TenantChatSectionDivider />

                <KBConfigPanel>
                  <KBSection>
                    <KBHeader>
                      <SettingLabel>Knowledge base</SettingLabel>
                      <KBFabRow>
                        {units.length > 1 ? (
                          <ApplyToAllDoorsBtn
                            size="small"
                            onClick={() =>
                              handleApplyKBToAllOtherDoors(unit.id, kb)
                            }
                          >
                            Apply to all other doors
                          </ApplyToAllDoorsBtn>
                        ) : (
                          <Tooltip
                            title="Add another door on Doors first. Then you can copy this saved list to your other doors in one step."
                            placement="top"
                          >
                            <span>
                              <ApplyToAllDoorsBtn size="small" disabled>
                                Apply to all other doors
                              </ApplyToAllDoorsBtn>
                            </span>
                          </Tooltip>
                        )}
                        <MagicLeaseBtn
                          type="primary"
                          icon={<StarOutlined />}
                          onClick={() => openLeaseFilePicker(unit.id)}
                          disabled={leaseLoading}
                          loading={leaseLoading}
                        >
                          Generate from lease
                        </MagicLeaseBtn>
                        <AddEntryPrimaryBtn
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => handleKBAdd(unit.id, kb)}
                        >
                          Add entry
                        </AddEntryPrimaryBtn>
                      </KBFabRow>
                    </KBHeader>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 13,
                        minHeight: "2.5rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: 0,
                        textAlign: "center",
                      }}
                    >
                      <span style={{ fontSize: 12, lineHeight: 1.4 }}>
                        Add FAQs, rules, or unit info that Leo AI will use to
                        answer tenant questions, or{" "}
                        <strong>generate from lease</strong> (PDF or DOCX) to
                        draft rules, then review and save.
                      </span>
                    </Text>

                    {leaseLoading && (
                      <LeaseReadingBox
                        role="status"
                        aria-live="polite"
                        aria-label="Reading lease file"
                      >
                        <LeaseReadingCenter>
                          <LeaseReadingSpinnerIcon spin aria-hidden />
                          <LeaseReadingMessage>
                            Leo is reading your lease…
                          </LeaseReadingMessage>
                        </LeaseReadingCenter>
                      </LeaseReadingBox>
                    )}

                    {kb.map((entry) => (
                      <PolicyCard key={entry.id} $draft={!!entry._draft}>
                        <PolicyCardTop>
                          <PolicyBadgeRow>
                            {entry._draft && (
                              <Tag color="gold" style={{ margin: 0 }}>
                                Draft
                              </Tag>
                            )}
                            {entry.source === "lease_extract" && !entry._draft && (
                              <Tag
                                color="green"
                                style={{
                                  margin: 0,
                                  border: "none",
                                  background: "rgba(44,95,45,0.12)",
                                }}
                              >
                                Lease
                              </Tag>
                            )}
                          </PolicyBadgeRow>
                          <PolicyHeaderRow>
                            <PolicyTitleInput
                              value={entry.title}
                              onChange={(e) =>
                                handleKBUpdate(
                                  unit.id,
                                  entry.id,
                                  "title",
                                  e.target.value
                                )
                              }
                              placeholder="e.g. Pet policy, parking, or quiet hours"
                              aria-label="Policy title"
                            />
                            <Button
                              icon={<DeleteOutlined />}
                              size="small"
                              danger
                              onClick={() =>
                                requestKBRemove(
                                  unit.id,
                                  entry.id,
                                  kb,
                                  entry.title
                                )
                              }
                            />
                          </PolicyHeaderRow>
                        </PolicyCardTop>
                        <TextArea
                          value={entry.content}
                          onChange={(e) =>
                            handleKBUpdate(
                              unit.id,
                              entry.id,
                              "content",
                              e.target.value
                            )
                          }
                          placeholder="Plain summary for tenants (e.g. where to park, when rent is due)."
                          autoSize={{ minRows: 2, maxRows: 8 }}
                        />
                      </PolicyCard>
                    ))}

                    {kb.length > 0 && (
                      <KBSaveBtn
                        type="primary"
                        onClick={() => handleKBSave(unit.id, kb)}
                        loading={saving[`kb-${unit.id}`]}
                      >
                        Save knowledge base
                      </KBSaveBtn>
                    )}
                  </KBSection>
                </KBConfigPanel>
              </>
            )}
          </PropertyCard>
        );
      })}
    </Wrapper>
  );
}

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 3rem;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  @media ${device.mobile} {
    gap: 1.35rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin: 0;
  line-height: 1.5;
  text-align: left;
  @media ${device.mobile} {
    line-height: 1.45;
  }
`;

const PropertyCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => prop !== "$bodyHidden",
})`
  border-radius: ${DOOR_CARD_RADIUS} !important;
  overflow: hidden;
  &&.ant-card {
    border: 2px solid rgba(44, 95, 45, 0.38) !important;
    background: #fff !important;
    box-shadow:
      0 1px 0 rgba(44, 95, 45, 0.08),
      0 4px 16px rgba(0, 0, 0, 0.07) !important;
  }
  .ant-card-head {
    /* Match inner curve of rounded border (12px outer − 2px border) */
    border-radius: ${DOOR_CARD_HEADER_RADIUS_INNER} ${DOOR_CARD_HEADER_RADIUS_INNER} 0 0 !important;
    background: ${FOREST_GREEN} !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.15) !important;
    padding: 1rem 1.25rem 1.1rem !important;
    min-height: 0 !important;
    color: #fff !important;
    .anticon,
    .anticon > svg,
    .anticon svg,
    .anticon path {
      color: #fff !important;
    }
    .ant-card-head-wrapper {
      overflow: visible !important;
      min-width: 0 !important;
    }
  }
  .ant-card-head-title {
    width: 100% !important;
    white-space: normal !important;
    overflow: visible !important;
  }
  .ant-card-head-title,
  .ant-card-head .ant-card-head-title {
    font-weight: 600 !important;
    font-family: var(--font-secondary) !important;
    color: #fff !important;
  }
  /* Ant Design defaults to single-line ellipsis on card titles; allow door address to wrap */
  .ant-card-head .ant-card-head-title,
  .ant-card-head-title {
    text-overflow: clip !important;
  }
  @media ${device.mobile} {
    .ant-card-head {
      padding: 0.75rem 0.9rem 0.85rem !important;
    }
    .ant-card-body {
      padding: 0.75rem 0.65rem 0.95rem !important;
    }
  }
  ${(p) =>
    p.$bodyHidden
      ? css`
          .ant-card-body {
            display: none !important;
            padding: 0 !important;
          }
          .ant-card-head {
            border-bottom: none !important;
            /* Collapsed: only the header shows; full inner radius on all corners */
            border-radius: ${DOOR_CARD_HEADER_RADIUS_INNER} !important;
          }
        `
      : ""}
`;

const PropertyCardHead = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  text-align: left;
  background: none;
  border: none;
  color: #fff !important;
  font: inherit;
  cursor: pointer;
  font-family: var(--font-secondary) !important;
  border-radius: 2px;
  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
  & * {
    color: inherit;
  }
  & .anticon,
  & .anticon svg,
  & .anticon path {
    color: #fff !important;
    fill: currentColor !important;
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const PropertyHeadText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  gap: 0.15rem;
  @media ${device.mobile} {
    width: 100%;
    max-width: 100%;
  }
`;

const PropertyNum = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #fff !important;
`;

const PropertyAddressLine = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.3;
  color: #fff !important;
  word-break: break-word;
`;

const HeaderStatusCluster = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.4rem;
  @media ${device.mobile} {
    padding-top: 0.12rem;
  }
`;

const LeoHeaderStatus = styled.span`
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.28rem 0.5rem 0.26rem;
  border-radius: 6px;
  line-height: 1;
  color: #fff !important;
  background: ${(p) =>
    p.$on ? "rgba(255, 255, 255, 0.28)" : "rgba(0, 0, 0, 0.2)"} !important;
  border: 1px solid
    ${(p) =>
    p.$on
      ? "rgba(255, 255, 255, 0.55)"
      : "rgba(255, 255, 255, 0.35)"} !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1);
  opacity: ${(p) => (p.$on ? 1 : 0.92)};
`;

const HeadChevronWrap = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: #fff !important;
  .anticon,
  .anticon svg,
  .anticon path {
    color: #fff !important;
    fill: #fff !important;
  }
  .anticon {
    font-size: 14px;
  }
`;

const AutomatedEmailsPanel = styled.div`
  text-align: left;
  padding: 0.85rem 1rem;
  background: #f8faf8;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
`;

const DoorsDivider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid #e5e7eb;
`;

const EmailCcSettingRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  text-align: left;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const EmailCcSwitchWrap = styled.div`
  flex-shrink: 0;
  padding-top: 0.1rem;

  @media ${device.mobile} {
    display: flex;
    justify-content: flex-start;
    padding-top: 0;
  }
`;

const EmailCcLabelBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const EmailCcCaption = styled.p`
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.4;
  color: #6b7280;
  font-family: var(--font-secondary);
  text-align: left;
`;

const SettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingLabel = styled.span`
  font-weight: 600;
  font-size: 0.9rem;
  font-family: var(--font-secondary);
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

/** Label aligns with the input row only; helper text sits in row 2 under the field (not the label). */
const ChatSlugField = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 1rem;
  row-gap: 0.35rem;
  margin-bottom: 1rem;
  align-items: center;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    column-gap: 0;
    row-gap: 0.45rem;
    align-items: stretch;
  }
`;

const ChatSlugHelper = styled.div`
  grid-column: 2;
  grid-row: 2;
  min-width: 0;

  @media ${device.mobile} {
    grid-column: 1;
    grid-row: auto;
  }
`;

const SlugRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  grid-column: 2;
  grid-row: 1;

  @media ${device.mobile} {
    grid-column: 1;
    grid-row: auto;
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
`;

/**
 * Beat Ant Design CSS-in-JS + globals.css so these panel CTAs match SettingLabel
 * (~0.9rem) instead of the larger default button type.
 */
const compactPanelBtnText = css`
  &&&.ant-btn,
  &&&.ant-btn-sm,
  &&&.ant-btn-primary,
  &&&.ant-btn-default {
    font-size: 0.9rem !important;
    font-weight: 600 !important;
  }

  &&&.ant-btn > span,
  &&&.ant-btn span:not(.anticon),
  &&&.ant-btn .ant-btn-icon,
  &&&.ant-btn .anticon {
    font-size: 0.9rem !important;
  }
`;

/** Ant Design's disabled primary can be overridden by theme/CSS; make inactive state obvious. */
const SlugSaveBtn = styled(Button)`
  ${compactPanelBtnText}
  &&.ant-btn {
    min-height: 2.2rem;
    height: 2.2rem !important;
    padding: 0 0.95rem !important;
    line-height: 1.1 !important;
  }
  &&.ant-btn-primary,
  &&.ant-btn {
    background: ${FOREST_GREEN} !important;
    border-color: ${FOREST_GREEN} !important;
    color: #fff !important;
  }
  &&.ant-btn-primary:hover:not(:disabled) {
    background: #244d25 !important;
    border-color: #244d25 !important;
    color: #fff !important;
  }
  &&&.ant-btn-primary:disabled,
  &&&.ant-btn-primary[disabled],
  &&&.ant-btn-primary.ant-btn-disabled {
    background: #d9d9d9 !important;
    border-color: #c8c8c8 !important;
    color: rgba(0, 0, 0, 0.28) !important;
    cursor: not-allowed !important;
    opacity: 1;
    box-shadow: none;
  }

  @media ${device.mobile} {
    width: 100%;
  }
`;

const ApplyToAllDoorsBtn = styled(Button)`
  ${compactPanelBtnText}
  &&.ant-btn {
    min-height: 0;
    height: auto !important;
    padding: 0.125rem 0.7rem !important;
    line-height: 1.3 !important;
  }
  &&.ant-btn {
    color: #234a24 !important;
    border: 1px solid rgba(44, 95, 45, 0.4) !important;
    background: #fff !important;
  }
  &&.ant-btn:hover:not(:disabled) {
    color: ${FOREST_GREEN} !important;
    border-color: ${FOREST_GREEN} !important;
    background: #f2f8f1 !important;
  }
  &&.ant-btn:disabled,
  &&.ant-btn[disabled] {
    color: rgba(0, 0, 0, 0.35) !important;
    border-color: #d9d9d9 !important;
    background: #f5f5f5 !important;
    cursor: not-allowed !important;
  }

  @media ${device.mobile} {
    width: 100% !important;
    &&.ant-btn {
      width: 100% !important;
      justify-content: center;
    }
  }
`;

const SlugInput = styled(Input)`
  border-radius: 8px !important;
  @media ${device.mobile} {
    width: 100% !important;
  }
`;

const ChatConfigPanel = styled.div`
  margin-top: 0.35rem;
  margin-bottom: 0.25rem;
  padding: 1rem 1.1rem 1.1rem;
  background: linear-gradient(180deg, #f4faf4 0%, #ecf2ec 100%);
  border: 1px solid rgba(44, 95, 45, 0.16);
  border-radius: 10px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);

  @media ${device.mobile} {
    padding: 0.85rem 0.7rem 0.95rem;
  }
`;

const MagicLinkBlock = styled.div`
  margin-top: 0.9rem;
  padding-top: 0.9rem;
  border-top: 1px solid rgba(44, 95, 45, 0.1);
`;

const VisitorLabelBlock = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(44, 95, 45, 0.1);
`;

/** Desktop: one row (label + hint, then input + save). Mobile: label row, then field row. */
const VisitorAddressSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  width: 100%;

  @media ${device.minTablet} {
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
  }
`;

const VisitorAddressLabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
  min-width: 0;

  ${(p) =>
    p.$opsChatCenter &&
    css`
      @media ${device.mobile} {
        justify-content: center;
        width: 100%;

        ${SettingLabel} {
          justify-content: center;
        }
      }
    `}
`;

const VisitorAddressInfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.45);
  font-size: 0.95rem;
  cursor: help;
  line-height: 1;
  flex-shrink: 0;

  &:hover,
  &:focus-visible {
    color: ${FOREST_GREEN};
  }
`;

const VisitorAddressFieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  width: 100%;

  @media ${device.minTablet} {
    flex: 0 0 auto;
    width: auto;
    margin-left: auto;
  }

  ${(p) =>
    p.$opsChatCenter &&
    css`
      @media ${device.mobile} {
        justify-content: center;
        margin-left: 0 !important;
        width: 100%;
      }
    `}
`;

/** Short name / label field: full width on mobile, modest width on larger screens. */
const VisitorAddressInputWrap = styled.div`
  min-width: 0;
  width: 100%;

  @media ${device.minTablet} {
    width: 280px;
    max-width: min(280px, 100%);
  }

  @media ${device.mobile} {
    width: 100% !important;
    max-width: 100% !important;
  }

  ${(p) =>
    p.$opsChatCenter &&
    css`
      @media ${device.mobile} {
        margin-left: 0;
        margin-right: 0;
      }
    `}
`;

const MagicLinkLabel = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  font-family: var(--font-secondary);
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
`;

const MagicUrlCodeRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.6rem;
  width: 100%;
  min-width: 0;
  flex-wrap: wrap;
  @media (min-width: 500px) {
    flex-wrap: nowrap;
  }
  @media ${device.mobile} {
    flex-direction: column;
    flex-wrap: nowrap;
    & > * {
      width: 100%;
      max-width: 100%;
    }
  }
`;

const MagicUrlCodeInput = styled(Input)`
  flex: 1 1 12rem;
  min-width: 0;
  @media ${device.mobile} {
    flex: 1 1 auto;
  }
  && .ant-input,
  & .ant-input,
  & input.ant-input {
    font-size: 12.5px !important;
    line-height: 1.45 !important;
    font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas,
      monospace !important;
    color: #183018 !important;
    background: #e4eee4 !important;
    border: 1px solid rgba(44, 95, 45, 0.3) !important;
    border-radius: 8px !important;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05) !important;
  }
  &&:hover .ant-input,
  &&:hover .ant-input:hover,
  & input.ant-input:hover {
    background: #d8e5d8 !important;
    border-color: rgba(44, 95, 45, 0.42) !important;
  }
  &&.ant-input-outlined,
  & .ant-input {
    min-height: 2.2rem;
  }
`;

const CopyUrlBtn = styled(Button)`
  ${compactPanelBtnText}
  &&.ant-btn,
  &&.ant-btn.ant-btn-sm {
    flex: 0 0 auto;
    min-height: 2.2rem;
    height: 2.2rem !important;
    padding: 0 0.9rem !important;
    line-height: 1.1 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  @media ${device.mobile} {
    &&.ant-btn,
    &&.ant-btn.ant-btn-sm {
      width: 100%;
      min-height: 2.4rem;
      height: auto !important;
    }
  }
  && .ant-btn-icon {
    line-height: 0;
  }
  &&.ant-btn-primary,
  &&.ant-btn {
    background: ${FOREST_GREEN} !important;
    border-color: ${FOREST_GREEN} !important;
    color: #fff !important;
  }
  &&.ant-btn-primary:disabled,
  &&.ant-btn:disabled {
    background: #d9d9d9 !important;
    border-color: #c8c8c8 !important;
    color: rgba(0, 0, 0, 0.32) !important;
  }
  &&.ant-btn-primary:hover:not(:disabled) {
    background: #244d25 !important;
    border-color: #244d25 !important;
    color: #fff !important;
  }
  &&.ant-btn-primary .anticon,
  &&.ant-btn-primary .anticon svg,
  &&.ant-btn .anticon,
  &&.ant-btn .anticon svg {
    color: #fff !important;
    fill: #fff !important;
  }
  &&.ant-btn-primary:disabled .anticon,
  &&.ant-btn:disabled .anticon {
    color: rgba(0, 0, 0, 0.32) !important;
  }
`;

const TenantChatSectionDivider = styled.div`
  display: block;
  height: 0;
  margin: 0.6rem 0 0.85rem;
  border: none;
  border-top: 1px solid rgba(44, 95, 45, 0.1);
  background: transparent;
`;

const KBConfigPanel = styled.div`
  padding: 0.9rem 1.05rem 1.05rem;
  background: #fbfdf9;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-left: 3px solid ${FOREST_GREEN};
  border-radius: 10px;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.7) inset;

  @media ${device.mobile} {
    padding: 0.8rem 0.65rem 0.9rem;
  }
`;

const KBSection = styled.div`
  margin-top: 0;
`;

const KBHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
  gap: 0.5rem;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const KBFabRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;

  @media ${device.mobile} {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    & > * {
      width: 100% !important;
      max-width: 100%;
      box-sizing: border-box;
    }
    & > span {
      display: block !important;
      width: 100% !important;
    }
    & .ant-btn {
      width: 100% !important;
    }
  }
`;

const MagicLeaseBtn = styled(Button)`
  ${compactPanelBtnText}
  &&.ant-btn {
    min-height: 0;
    height: auto !important;
    padding: 0.125rem 0.95rem !important;
    line-height: 1.35 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 0.4rem;
  }
  && .ant-btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-inline-end: 0 !important;
    line-height: 0;
  }
  && > span:not(.ant-btn-icon) {
    text-align: left;
  }
  background: ${FOREST_GREEN} !important;
  border-color: ${FOREST_GREEN} !important;
  color: #fff !important;
  .anticon,
  .anticon path {
    color: #fff !important;
  }
  &:hover:not(:disabled):not([disabled]) {
    background: #244d25 !important;
    border-color: #244d25 !important;
    color: #fff !important;
  }
  &&.ant-btn-primary:disabled,
  &&.ant-btn-primary[disabled],
  &&.ant-btn-primary.ant-btn-disabled {
    background: #d9d9d9 !important;
    border-color: #c8c8c8 !important;
    color: rgba(0, 0, 0, 0.28) !important;
    cursor: not-allowed !important;
  }
  &&.ant-btn-primary.ant-btn-loading {
    background: ${FOREST_GREEN} !important;
    border-color: #244d25 !important;
    color: #fff !important;
    cursor: wait !important;
    opacity: 0.92;
  }
  &&.ant-btn-primary.ant-btn-loading .anticon,
  &&.ant-btn-primary.ant-btn-loading
    .ant-btn-loading-icon
    .anticon {
    color: #fff !important;
  }
`;

const LeaseReadingBox = styled.div`
  box-sizing: border-box;
  margin-bottom: 1rem;
  padding: clamp(1.25rem, 4vw, 2rem) clamp(0.85rem, 3vw, 1.25rem);
  min-height: clamp(11rem, 38vh, 17.5rem);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background: #f8faf6;
  border: 1px solid rgba(44, 95, 45, 0.2);
  border-radius: 10px;
`;

const LeaseReadingCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(0.65rem, 2.5vw, 1rem);
  width: 100%;
  max-width: 22rem;
  margin: 0 auto;
  text-align: center;
`;

const LeaseReadingSpinnerIcon = styled(LoadingOutlined)`
  &&.anticon {
    font-size: clamp(2.75rem, 14vw, 4.25rem);
    color: ${FOREST_GREEN};
  }
`;

const LeaseReadingMessage = styled(Text).attrs({ type: "secondary" })`
  && {
    display: block;
    margin: 0;
    font-size: clamp(0.875rem, 2.8vw, 1rem);
    line-height: 1.45;
    max-width: 100%;
  }
`;

const AddEntryPrimaryBtn = styled(Button)`
  ${compactPanelBtnText}
  &&.ant-btn {
    min-height: 0;
    height: auto !important;
    padding: 0.125rem 0.95rem !important;
    line-height: 1.35 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 0.4rem;
  }
  && .ant-btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-inline-end: 0 !important;
    line-height: 0;
  }
  && > span:not(.ant-btn-icon) {
    text-align: left;
  }
  background: #2c5f2d !important;
  border-color: #2c5f2d !important;
  color: #fff !important;
  .anticon,
  .anticon path {
    color: #fff !important;
  }
  &:hover {
    background: #244d25 !important;
    border-color: #244d25 !important;
    color: #fff !important;
  }
`;

const KBSaveBtn = styled(Button)`
  display: block;
  margin: 0.5rem auto 0;
  &&.ant-btn {
    min-height: 44px;
    height: auto !important;
    padding: 0.78rem 1.4rem !important;
    line-height: 1.45 !important;
  }
  @media ${device.mobile} {
    width: 100%;
    margin: 0.5rem 0 0;
    &&.ant-btn {
      width: 100% !important;
    }
  }
  background: #2c5f2d !important;
  border-color: #2c5f2d !important;
  color: #fff !important;
  .anticon,
  .anticon path {
    color: #fff !important;
  }
  &:hover:not(:disabled):not([disabled]) {
    background: #244d25 !important;
    border-color: #244d25 !important;
    color: #fff !important;
  }
  &&&.ant-btn-primary:disabled,
  &&&.ant-btn-primary[disabled],
  &&&.ant-btn-primary.ant-btn-disabled {
    background: #d9d9d9 !important;
    border-color: #c8c8c8 !important;
    color: rgba(0, 0, 0, 0.28) !important;
    cursor: not-allowed !important;
    opacity: 1;
    box-shadow: none;
  }
`;

const PolicyCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 1rem 1.1rem 1.1rem;
  background: #fff;
  border: 1px solid
    ${(p) =>
      p.$draft ? "rgba(212, 160, 23, 0.5)" : "rgba(44, 95, 45, 0.18)"};
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 0.75rem;
  box-sizing: border-box;
  .ant-input,
  .ant-input-textarea-in-inner {
    border-radius: 8px !important;
  }
`;

const PolicyCardTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const PolicyBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  min-height: 22px;
`;

const PolicyHeaderRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  width: 100%;
  min-width: 0;
`;

const PolicyTitleInput = styled(Input)`
  flex: 1;
  min-width: 0;
  font-size: 1.05rem !important;
  font-weight: 600 !important;
  font-family: var(--font-secondary, "Inter", sans-serif) !important;
  line-height: 1.3 !important;
  padding: 0.5rem 0.65rem !important;
  border: none !important;
  background: rgba(44, 95, 45, 0.05) !important;
  color: var(--color-text) !important;
  &:hover,
  &:focus {
    background: rgba(44, 95, 45, 0.08) !important;
    box-shadow: none !important;
  }
`;

const AddDoorsNavButton = styled(Button)`
  background: #2c5f2d !important;
  border-color: #2c5f2d !important;
  color: #fff !important;

  .anticon,
  .anticon svg,
  .anticon path {
    color: #fff !important;
    fill: currentColor !important;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 24px;
  text-align: center;
`;

const EmptyTitle = styled.p`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
  font-family: var(--font-primary);
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-light);
  max-width: 320px;
  line-height: 1.5;
  font-family: var(--font-secondary);
`;
